// MACHINERY V2 (Stroje) — V4 shop-floor wall: the kiosk clock (M2.14).
//
// >>> THIS IS THE ONLY REPEATING TIMER M2.14 ADDS, AND IT TOUCHES NO
// NETWORK. <<< (The exit hint's fade uses a one-shot setTimeout per pointer
// burst — WallExitHint — which idles at zero on an unattended screen.)
//
// The tiles are driven by the EXISTING 60 s fleet poll — `useFleetTelemetryFeed`
// (fleetTelemetry.ts:206-273), hoisted once in FleetPage via useFleetPageData,
// exactly as the V1 cards, the V2 rows and the V3 table are. The wall adds no
// second poller, no second fetch and no second endpoint; it is another BODY
// hanging off the same single hook (see FleetPage's "ONE POLLER PER PAGE"
// header).
//
// What the wall does need that no other view does is a SECOND HAND. The
// artboard prints `06:49:22 · 28.04.2026` next to a live dot, and a clock that
// only advanced when telemetry arrived would sit frozen for up to a minute —
// on the one surface whose entire job is to look alive from across a hall. So:
// one setInterval, 1000 ms, pure `new Date()`, no request of any kind.
//
// It is deliberately NOT folded into the telemetry hook: that hook's interval
// is the poll cadence and is due to be DELETED wholesale when the deferred
// SignalR push lands (fleetTelemetry.ts:36-42). A display clock must not be a
// casualty of that removal.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useEffect, useState } from "react";

/** Tick cadence. Seconds are printed, so anything slower visibly stutters. */
export const WALL_CLOCK_TICK_MS = 1000;

export interface WallClock {
  /** 24h, zero-padded, LOCALE-formatted — `06:49:22` on every language we
   *  ship, since `hour12: false` is passed explicitly. */
  time: string;
  /**
   * Two-digit day and month, four-digit year, LOCALE-formatted — so the order
   * and the separator are the reader's, not ours: `28.04.2026` on sk/cs (the
   * artboard's exact form), `04/28/2026` on en-US, `28/04/2026` on en-GB. An
   * earlier version of this comment promised "day-first" for all of them,
   * which `toLocaleDateString` does not do and this file never asked it to.
   *
   * The COMMENT was corrected rather than the code: the wall renders in the
   * user's own language, and hard-coding the Slovak order onto an English UI
   * would make one of the two audiences read a date in a foreign convention on
   * a screen whose entire job is instant legibility from across a hall.
   */
  date: string;
}

const format = (now: Date, lang: string): WallClock => ({
  time: now.toLocaleTimeString(lang, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }),
  date: now.toLocaleDateString(lang, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }),
});

/**
 * A ticking wall clock.
 *
 * The interval is cleared on unmount, so leaving the wall (a view switch, a
 * route change, the section unmounting) stops it. It is intentionally NOT
 * paused on `document.hidden`: unlike a poll it costs no request and no server
 * work, and a clock that resumed showing the moment it was hidden would be a
 * worse lie than one that kept counting.
 */
export const useWallClock = (lang: string): WallClock => {
  const [clock, setClock] = useState<WallClock>(() => format(new Date(), lang));

  useEffect(() => {
    // Re-format immediately so a language switch does not wait a whole second.
    setClock(format(new Date(), lang));
    const timer = setInterval(() => setClock(format(new Date(), lang)), WALL_CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, [lang]);

  return clock;
};

export default useWallClock;
